const _excluded = ["className", "complete", "invalid"],
      _excluded2 = ["valueState"];

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { useReducer, useState } from 'react';
import cn from 'classnames';
import { useGlobalContext } from 'seagull';
import typography from 'seagull/foundations/typography.module.scss';
import colors from 'seagull/foundations/colors.module.scss';
import { Button, Callout, Typography } from 'seagull';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import { handleRequest } from "../../infra/utils";
import styles from "./ConnectCreditCard.module.scss";
import mpmStyles from "./ManagePaymentMethods.module.scss";
import { isNonEmptyInput } from "../../infra/utils";
import { Intent } from "../../infra/access-control";
import { RelationshipProgressStatus } from "../../models/relationship";
import { getGeneralErrorText } from "./errors";
import { SlackUserGroups } from "../../utils/slack-client";
const emptyExpiryError = 'Please enter a valid card expiry date';
const emptyCvvError = 'Please enter a valid CVV';
const emptyCardNumberError = 'Please enter a valid card number'; // https://stripe.com/docs/js/appendix/style

const styleObj = {
  base: {
    fontFamily: typography.fontFamilyLight,
    fontSize: typography.fontSizeP1,
    fontSmoothing: 'antialiased',
    color: colors.dark700,
    ':-webkit-autofill': {
      color: colors.dark700
    },
    '::placeholder': {
      color: colors.dark700
    }
  },
  invalid: {
    color: colors.dark700
  }
}; // https://stripe.com/docs/js/elements_object/create_element?type=card

const classesObj = {
  complete: mpmStyles.complete
};

const getInitialValueState = () => ({
  value: '',
  error: null,
  pristine: true,
  touched: false
});

const getInitialStripeValue = function getInitialStripeValue() {
  let errorMessage = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return {
    touched: false,
    valid: false,
    errorMessage
  };
};

function mapStripeErrorToMessage(error) {
  if (error.code === 'card_declined') {
    if (error.decline_code === 'invalid_account') {
      return 'Credit card connection failed. The card, or account the card is connected to, is invalid. Contact your card issuer or connect a different payment method.';
    } else if (error.decline_code === 'transaction_not_allowed') {
      return 'The card was declined. Contact your card issuer or connect a different payment method.';
    }
  } else if (error.code === 'incorrect_cvc' || error.code === 'invalid_cvc') {
    return "The card's security code is incorrect. Check the card's security code or use a different card.";
  } else if (error.code === 'setup_intent_authentication_failure') {
    return "The provided credit card has failed authentication. Please check the card's information or connect a different payment method.";
  } else if (error.code === 'incorrect_number') {
    return "The card number is incorrect. Check the card's number or use a different card.";
  }

  return null;
}

const stripeStateReducer = (state, action) => {
  const {
    type,
    errorMessage = ''
  } = action;

  switch (type) {
    case 'error':
      return _objectSpread(_objectSpread({}, state), {}, {
        valid: false,
        errorMessage
      });

    case 'valid':
      return state.valid ? state : _objectSpread(_objectSpread({}, state), {}, {
        valid: true
      });

    case 'blur':
      return state.touched ? state : _objectSpread(_objectSpread({}, state), {}, {
        touched: true
      });

    default:
      return state;
  }
};

function validateNameOnCard(value) {
  return isNonEmptyInput(value) ? '' : 'Please specify the name on the card.';
}

function validateZipCode(value) {
  let minLength = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 5;
  value = value.trim();
  let error = '';

  if (!isNonEmptyInput(value)) {
    error = 'Please specify the ZIP code.';
  } else if (value.length < minLength) {
    error = 'Please enter a valid 5-digit ZIP Code';
  }

  return error;
}

const Label = props => {
  const {
    children,
    size = 'small'
  } = props;
  return /*#__PURE__*/React.createElement(Typography, {
    className: cn(mpmStyles.label, {
      [mpmStyles.mediumSize]: size === 'medium'
    }),
    variant: size === 'small' ? 'paragraph2' : 'paragraph1'
  }, children);
};

const Field = props => /*#__PURE__*/React.createElement("div", {
  className: cn(mpmStyles.field, {
    [mpmStyles.marginTop]: !props.isFirst
  }, props.className)
}, props.children);

const ErrorText = props => /*#__PURE__*/React.createElement(Typography, _extends({}, props, {
  className: cn(mpmStyles.error, props.className),
  variant: "paragraph2",
  skin: "error"
}));

const BaseInput = /*#__PURE__*/React.forwardRef(function Input(props, ref) {
  const {
    className,
    complete,
    invalid
  } = props,
        rest = _objectWithoutProperties(props, _excluded);

  return /*#__PURE__*/React.createElement("input", _extends({}, rest, {
    className: cn(mpmStyles.input, {
      [mpmStyles.complete]: complete,
      [mpmStyles.invalid]: invalid
    }, className),
    ref: ref
  }));
});

const Input = props => {
  const {
    valueState
  } = props,
        rest = _objectWithoutProperties(props, _excluded2);

  return /*#__PURE__*/React.createElement(BaseInput, _extends({}, rest, {
    complete: valueState.touched && !valueState.error,
    invalid: valueState.touched && !!valueState.error,
    value: valueState.value
  }));
};

/**
 * Renders a form for filling out credit-card details.
 * Expects to be wrapped in an Elements provider from @stripe/react-stripe-js.
 */
export default function ConnectCreditCard(props) {
  const {
    relationshipId,
    vendorId,
    disabledReason,
    updateProgress,
    onConnect
  } = props;
  const elements = useElements();
  const stripe = useStripe();
  const {
    slackClient: {
      sendCsNotificationsMessage
    }
  } = useIoContext();
  const globalContext = useGlobalContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const stripeLoadingFailed = useStore(state => state.stripeLoadingFailed);
  const [cardNum, cardNumDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyCardNumberError));
  const [expiry, expiryDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyExpiryError));
  const [cvv, cvvDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyCvvError));
  const [nameOnCard, setNameOnCard] = useState(getInitialValueState);
  const [zipCode, setZipCode] = useState(getInitialValueState);
  const [apiError, setApiError] = useState();
  const allFieldsValid = cardNum.valid && expiry.valid && cvv.valid && !nameOnCard.pristine && !nameOnCard.error && !zipCode.pristine && !zipCode.error;

  const connectCreditCard = async event => {
    setApiError(undefined);
    event.preventDefault();
    if (!allFieldsValid || !elements || !stripe) return;
    const cardNumberEl = elements.getElement('cardNumber');
    if (!cardNumberEl) return;
    logBI(10806);
    const loaderName = 'creditCard';
    globalContext.startLoader(loaderName);

    const handleError = message => {
      globalContext.stopLoader(loaderName);
      setApiError(message != null ? message : getGeneralErrorText());
      logBI(10819);
    };

    const [clientSecret,, hasError] = await handleRequest(api.paymentMethod.createSetupIntent(vendorId));

    if (hasError) {
      return handleError();
    }

    const setupIntentResult = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardNumberEl,
        billing_details: {
          name: nameOnCard.value,
          address: {
            postal_code: zipCode.value
          }
        }
      }
    });

    if (setupIntentResult.error) {
      const mappedMessage = mapStripeErrorToMessage(setupIntentResult.error);

      if (mappedMessage == null && setupIntentResult.error.code) {
        const text = "add credit-card error (relationshipId=".concat(relationshipId, ", vendorId=").concat(vendorId, ") - ").concat(JSON.stringify(setupIntentResult.error), " - ").concat(SlackUserGroups.ProductTeam, " please provide texts for this failure :pray:");
        sendCsNotificationsMessage(text);
      }

      logBI(10007, {
        success: false
      });
      return handleError(mappedMessage);
    }

    logBI(10007, {
      success: true
    });
    const paymentMethodId = setupIntentResult.setupIntent.payment_method;

    if (!paymentMethodId) {
      return handleError();
    }

    const [fundingSource,, hasFundingSourceErr] = await handleRequest(api.paymentMethod.addToBusiness(paymentMethodId, vendorId));

    if (hasFundingSourceErr) {
      return handleError();
    }

    if (updateProgress) {
      api.relationship.updateProgressStatus(relationshipId, RelationshipProgressStatus.PaymentMethod);
    }

    logBI(10808);
    globalContext.stopLoader(loaderName);
    onConnect(fundingSource.id);
  };

  return /*#__PURE__*/React.createElement("form", {
    onSubmit: connectCreditCard
  }, /*#__PURE__*/React.createElement(Field, {
    isFirst: true
  }, /*#__PURE__*/React.createElement(Label, null, "Name on card"), /*#__PURE__*/React.createElement(Input, {
    valueState: nameOnCard,
    placeholder: "Enter your name",
    "data-testid": "manage-payment-methods-credit-card-name",
    className: cn(mpmStyles.input, {
      [mpmStyles.invalid]: nameOnCard.touched && nameOnCard.error
    }),
    disabled: !!disabledReason,
    onChange: ev => {
      setApiError(undefined);
      const error = validateNameOnCard(ev.currentTarget.value);
      setNameOnCard(_objectSpread(_objectSpread({}, nameOnCard), {}, {
        value: ev.currentTarget.value,
        pristine: false,
        error
      }));
    },
    onBlur: () => {
      const error = validateNameOnCard(nameOnCard.value);
      setNameOnCard(_objectSpread(_objectSpread({}, nameOnCard), {}, {
        error,
        touched: true
      }));
    }
  }), nameOnCard.error && nameOnCard.touched && /*#__PURE__*/React.createElement(ErrorText, {
    "data-testid": "connect-cc-name-error"
  }, nameOnCard.error)), /*#__PURE__*/React.createElement(Field, null, /*#__PURE__*/React.createElement(Label, null, "Card number"), /*#__PURE__*/React.createElement(CardNumberElement, {
    className: cn(mpmStyles.input, {
      [mpmStyles.invalid]: cardNum.touched && !cardNum.valid && cardNum.errorMessage
    }),
    options: {
      disabled: !!disabledReason,
      style: styleObj,
      classes: classesObj,
      placeholder: '0000-0000-0000-0000'
    },
    onChange: event => {
      const {
        complete,
        error,
        empty
      } = event;
      setApiError(undefined);

      if (complete) {
        cardNumDispatch({
          type: 'valid'
        });
      } else {
        cardNumDispatch({
          type: 'error',
          errorMessage: error ? error.message : empty ? emptyCardNumberError : ''
        });
      }
    },
    onBlur: () => cardNumDispatch({
      type: 'blur'
    })
  }), cardNum.touched && !cardNum.valid && /*#__PURE__*/React.createElement(ErrorText, {
    "data-testid": "connect-cc-num-error"
  }, cardNum.errorMessage)), /*#__PURE__*/React.createElement("div", {
    className: styles.ccExpirationRow
  }, /*#__PURE__*/React.createElement(Field, null, /*#__PURE__*/React.createElement(Label, null, "Expiration date"), /*#__PURE__*/React.createElement(CardExpiryElement, {
    "data-testid": "manage-payment-methods-credit-card-expiry",
    className: cn(mpmStyles.input, {
      [mpmStyles.invalid]: expiry.touched && expiry.errorMessage
    }),
    options: {
      disabled: !!disabledReason,
      style: styleObj,
      classes: classesObj,
      placeholder: 'MM/YY'
    },
    onChange: event => {
      const {
        complete,
        error,
        empty
      } = event;
      setApiError(undefined);

      if (complete) {
        expiryDispatch({
          type: 'valid'
        });
      } else {
        expiryDispatch({
          type: 'error',
          errorMessage: error ? error.message : empty ? emptyExpiryError : ''
        });
      }
    },
    onBlur: () => expiryDispatch({
      type: 'blur'
    })
  }), expiry.touched && !expiry.valid && /*#__PURE__*/React.createElement(ErrorText, {
    "data-testid": "connect-cc-expiry-error"
  }, expiry.errorMessage)), /*#__PURE__*/React.createElement(Field, null, /*#__PURE__*/React.createElement(Label, null, "CVV"), /*#__PURE__*/React.createElement(CardCvcElement, {
    className: cn(mpmStyles.input, {
      [mpmStyles.invalid]: cvv.touched && cvv.errorMessage
    }),
    options: {
      disabled: !!disabledReason,
      style: styleObj,
      classes: classesObj,
      placeholder: 'CVV'
    },
    onChange: event => {
      const {
        complete,
        error,
        empty
      } = event;
      setApiError(undefined);

      if (complete) {
        cvvDispatch({
          type: 'valid'
        });
      } else {
        cvvDispatch({
          type: 'error',
          errorMessage: error ? error.message : empty ? emptyCvvError : ''
        });
      }
    },
    onBlur: () => cvvDispatch({
      type: 'blur'
    })
  }), cvv.touched && !cvv.valid && /*#__PURE__*/React.createElement(ErrorText, {
    "data-testid": "connect-cc-cvv-error"
  }, cvv.errorMessage))), /*#__PURE__*/React.createElement(Field, null, /*#__PURE__*/React.createElement(Label, null, "Zip code"), /*#__PURE__*/React.createElement(Input, {
    valueState: zipCode,
    type: "text",
    "data-testid": "connect-cc-zip",
    disabled: !!disabledReason,
    className: cn(mpmStyles.input, {
      [mpmStyles.invalid]: zipCode.touched && nameOnCard.error
    }),
    onChange: ev => {
      setApiError(undefined);
      const value = ev.target.value.trim();
      const error = validateZipCode(value, -1);
      setZipCode(_objectSpread(_objectSpread({}, zipCode), {}, {
        value,
        pristine: false,
        error
      }));
    },
    onBlur: () => {
      const error = validateZipCode(zipCode.value, -1);
      setZipCode(_objectSpread(_objectSpread({}, zipCode), {}, {
        touched: true,
        error
      }));
    }
  }), zipCode.error && zipCode.touched && /*#__PURE__*/React.createElement(ErrorText, {
    "data-testid": "connect-cc-zip-error"
  }, zipCode.error)), /*#__PURE__*/React.createElement(Field, null, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    size: "small",
    disabled: !!(!allFieldsValid || apiError || stripeLoadingFailed),
    "data-testid": "manage-payment-methods-add-credit-card",
    intent: Intent.AddPaymentMethod,
    tooltip: {
      placement: 'top',
      title: disabledReason ? /*#__PURE__*/React.createElement("div", {
        "data-testid": "add-cc-disabled-reason"
      }, disabledReason) : '',
      'data-testid': 'connect-cc-submit-btn-tooltip',
      disableFocusListener: disabledReason == null,
      disableHoverListener: disabledReason == null,
      disableTouchListener: disabledReason == null
    }
  }, "Connect credit card"), (apiError || stripeLoadingFailed) && /*#__PURE__*/React.createElement(Callout, {
    className: styles.error,
    intent: "error",
    "data-testid": "manage-payment-methods-cc-error"
  }, stripeLoadingFailed ? 'An error has occurred. Refresh the page and try again' : apiError)));
}